import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from './auth';
import { format } from 'date-fns';
import {
  Paper,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PublicIcon from '@mui/icons-material/Public';

const API_BASE = 'https://fp-api.focuspointintl.com/api/fetchRiskLineAlerts';
const DAY_OPTIONS = [7, 14, 30, 60, 90];

const riskStyles = {
  High: { bg: '#c62828', light: '#ffebee', border: '#ef9a9a' },
  Elevated: { bg: '#e65100', light: '#fff3e0', border: '#ffcc80' },
  Moderate: { bg: '#f9a825', light: '#fffde7', border: '#fff176' },
  Low: { bg: '#2e7d32', light: '#e8f5e9', border: '#a5d6a7' },
  Unknown: { bg: '#546e7a', light: '#eceff1', border: '#b0bec5' },
};

const getRiskStyle = (level) => riskStyles[level] || riskStyles.Unknown;

const formatAlertDate = (dateStr) => {
  if (!dateStr) return 'Unknown date';
  return format(new Date(dateStr), 'MMMM d, yyyy');
};

const sortAlertsDesc = (alerts) =>
  [...alerts].sort((a, b) => new Date(b.CleanDate) - new Date(a.CleanDate));

const buildCopyText = (alerts, days) => {
  const sorted = sortAlertsDesc(alerts);
  const generated = format(new Date(), 'MMMM d, yyyy \'at\' h:mm a');
  const divider = '─'.repeat(60);

  const header = [
    'FOCUSPOINT TRAVEL ALERTS',
    `Period: Last ${days} day${days === 1 ? '' : 's'}  ·  ${sorted.length} alert${sorted.length === 1 ? '' : 's'}`,
    `Generated: ${generated}`,
    divider,
    '',
  ].join('\n');

  const body = sorted
    .map((alert, index) => {
      const date = formatAlertDate(alert.CleanDate);
      const risk = (alert.risk_level || 'Unknown').toUpperCase();
      return [
        `${index + 1}. ${alert.FullCountryName}  ·  ${risk}  ·  ${date}`,
        alert.title || '(No title)',
        '',
        alert.text || '',
        '',
        divider,
        '',
      ].join('\n');
    })
    .join('\n');

  return header + body;
};

const TravelAlertsPage = () => {
  const [days, setDays] = useState(7);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const { accounts } = useMsal();
  const navigate = useNavigate();

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error('Authentication failed');

      const url = days === 7 ? API_BASE : `${API_BASE}?days=${days}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error('Failed to fetch travel alerts');

      const result = await response.json();
      if (!result.success) throw new Error('API returned an unsuccessful response');

      setAlerts(sortAlertsDesc(result.data || []));
    } catch (err) {
      setError(err.message);
      setAlerts([]);
      console.error('Travel alerts error:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (accounts.length === 0) {
      navigate('/');
    } else {
      fetchAlerts();
    }
  }, [accounts, navigate, fetchAlerts]);

  const copyText = useMemo(() => buildCopyText(alerts, days), [alerts, days]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <PublicIcon />
              <Typography variant="h5" component="h1" fontWeight={600}>
                Travel Alerts
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Recent Riskline alerts — copy the full report in one click
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="days-select-label" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Period
              </InputLabel>
              <Select
                labelId="days-select-label"
                value={days}
                label="Period"
                onChange={(e) => setDays(e.target.value)}
                sx={{
                  color: 'white',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
                  '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
                }}
              >
                {DAY_OPTIONS.map((d) => (
                  <MenuItem key={d} value={d}>
                    Last {d} days
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}
              disabled={loading || alerts.length === 0}
              sx={{
                bgcolor: 'white',
                color: '#1a237e',
                fontWeight: 600,
                '&:hover': { bgcolor: '#f5f5f5' },
                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.5)' },
              }}
            >
              {copied ? 'Copied!' : 'Copy All'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && alerts.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No travel alerts found in the last {days} days.
          </Typography>
        </Paper>
      )}

      {!loading && alerts.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {alerts.length} alert{alerts.length === 1 ? '' : 's'} · sorted newest first
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            {alerts.map((alert, index) => {
              const style = getRiskStyle(alert.risk_level);
              return (
                <Paper
                  key={`${alert.FullCountryName}-${alert.CleanDate}-${index}`}
                  elevation={1}
                  sx={{
                    overflow: 'hidden',
                    borderLeft: `4px solid ${style.bg}`,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <Box sx={{ px: 2.5, py: 2, bgcolor: style.light, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} color="#1a237e">
                        {alert.FullCountryName}
                      </Typography>
                      <Chip
                        label={alert.risk_level || 'Unknown'}
                        size="small"
                        sx={{ bgcolor: style.bg, color: 'white', fontWeight: 600, fontSize: '0.75rem' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {formatAlertDate(alert.CleanDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {alert.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                      {alert.text}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              bgcolor: '#fafafa',
              borderColor: '#e0e0e0',
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="overline" color="text.secondary" letterSpacing={1}>
                Copy-ready format
              </Typography>
              <Button
                size="small"
                startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                onClick={handleCopy}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Box>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                bgcolor: 'white',
                border: '1px solid #e8e8e8',
                borderRadius: 1,
                fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                fontSize: '0.8rem',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 400,
                overflow: 'auto',
                color: '#333',
              }}
            >
              {copyText}
            </Box>
          </Paper>
        </>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Full report copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default TravelAlertsPage;
